import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@parrot-software/design-system';
import { BadgeCheck, TriangleAlert } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';

type StatusKey = 'paid' | 'pending' | 'overdue' | 'failed' | 'refunded';
type BadgeVariant = 'outline' | 'secondary';

type StatusAppearance = {
  variant: BadgeVariant;
  className: string;
  Icon?: ComponentType<{ className?: string }>;
};

const STATUS_APPEARANCE: Record<StatusKey, StatusAppearance> = {
  paid: {
    variant: 'outline',
    className: 'bg-bg-success text-success',
    Icon: BadgeCheck,
  },
  pending: {
    variant: 'outline',
    className: 'bg-bg-warning text-warning hover:bg-bg-warning',
    Icon: TriangleAlert,
  },
  // A charge already owed, unlike `pending`, which is the next one coming.
  overdue: {
    variant: 'outline',
    className: 'bg-destructive/10 text-destructive border-destructive',
    Icon: TriangleAlert,
  },
  failed: {
    variant: 'outline',
    className: 'bg-destructive/10 text-destructive',
  },
  refunded: {
    variant: 'outline',
    className: 'bg-bg-success text-success',
  },
};

/** Maps raw API status values to the canonical translation key used in locales. */
const STATUS_TRANSLATION_KEY: Record<string, StatusKey> = {
  pre_paid: 'paid',
  paid: 'paid',
  success: 'paid',
  pending: 'pending',
  overdue: 'overdue',
  failed: 'failed',
  refunded: 'refunded',
};

interface PaymentStatusBadgeProps {
  status: string;
  label?: string;
  showIcon?: boolean;
}

/**
 * PaymentStatusBadge - Colored badge for payment/license status.
 *
 * Normalizes the raw status, resolves the translated label from the
 * `paymentHistory:status.*` namespace (unless a `label` override is provided),
 * and picks the matching variant/styles. Falls back to an outline badge
 * with the raw status when unmapped. The status icon is opt-in via `showIcon`
 * (used only by the license badge — payment-history badges render text only).
 *
 * @param {Object} props - Component properties
 * @param {string} props.status - Raw status value (API payment status or license sub-status)
 * @param {string} [props.label] - Optional label override (e.g. "Por pagar" for the license badge)
 * @param {boolean} [props.showIcon] - Whether to render the status icon (license badges only)
 * @returns {React.ReactElement} Styled badge with the localized label
 */
export const PaymentStatusBadge = ({ status, label, showIcon = false }: PaymentStatusBadgeProps) => {
  const { t } = useTranslation('paymentHistory');

  const translationKey = STATUS_TRANSLATION_KEY[status.toLowerCase()];
  const appearance = translationKey ? STATUS_APPEARANCE[translationKey] : undefined;
  const resolvedLabel = label ?? (translationKey ? t(`status.${translationKey}`) : status);
  const Icon = showIcon ? appearance?.Icon : undefined;

  return (
    <Badge
      className={cn('font-semibold w-fit gap-1 border-transparent', appearance?.className)}
      variant={appearance?.variant ?? 'outline'}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {resolvedLabel}
    </Badge>
  );
};
